import { useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { SourceDocument } from "../../../../api/content/sourcesApi";
import { formatDocumentVersionRef } from "../../../../shared/references/contentReferences";
import type { EvidenceMark } from "../../../annotation/textEvidence";
import type { ChatReference } from "../../../chatReferences";
import { absorbNamedIndividuals, groupIndividuals } from "../../../knowledgeData/abox/aboxViewModel";
import DocumentParts, { type DrawnAnnotations } from "../parts/DocumentParts";
import PartStatements from "./PartStatements";
import { itemKeys, regestParts, type PlacedStatement, type RegestResult } from "./regestParts";

interface TextWithStatementsProps {
  readonly document: Pick<SourceDocument, "ref" | "fullText" | "segments" | "metadata">;
  /** The statements drawn, accepted or not (F154); evidence on other documents or versions is not drawn. */
  readonly results: readonly RegestResult[];
  /** The annotation set drawn on the text; `null` draws none. */
  readonly annotations: DrawnAnnotations | null;
  /** Where selecting text asks the agent about it (F96); `undefined` without a project. */
  readonly askInProject: string | undefined;
  /** What a selection in the text also sends, such as the annotation set on screen (PC-6). */
  readonly askWith: Omit<ChatReference, "quote"> | null;
  /** What a statement's line also shows, such as the parts of a vita it came from. */
  readonly statementExtra?: (statement: PlacedStatement) => ReactNode;
}

/**
 * A document's text with the statements about it (O-1), for every page that shows instance data:
 * the Documents page, a result's page and its review steps (F154: one view, before and after
 * acceptance). The text shows once, part by part; each statement sits beside the part that holds
 * most of its words, once (`absorbNamedIndividuals`); one with no words in this text follows it.
 * Pointing at a statement or its words highlights both; clicking keeps them highlighted and
 * brings the other side into view; Escape lets go.
 */
export default function TextWithStatements({ document, results, annotations, askInProject, askWith, statementExtra }: TextWithStatementsProps) {
  const { t, i18n } = useTranslation();
  const [pointed, setPointed] = useState<readonly string[] | null>(null);
  const [selected, setSelected] = useState<readonly string[] | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const shown = formatDocumentVersionRef(document.ref);
  const language = i18n.resolvedLanguage ?? "en";
  const individuals = useMemo(() => groupIndividuals(results.flatMap((result) => result.statements), language), [results, language]);
  const { byPart, unplaced } = useMemo(() => {
    const placement = regestParts(shown, document.segments, results);
    const absorbed = (statements: readonly PlacedStatement[]) => absorbNamedIndividuals(statements, individuals, () => false);
    return {
      byPart: new Map([...placement.parts].map(([segmentPath, statements]) => [segmentPath, absorbed(statements)])),
      unplaced: absorbed(placement.unplaced),
    };
  }, [shown, document.segments, results, individuals]);
  // Every statement key leads to all the keys of the line that shows it.
  const lineKeys = useMemo(() => new Map([...byPart.values(), unplaced].flat().flatMap((item) => itemKeys(item).map((key) => [key, itemKeys(item)] as const))), [byPart, unplaced]);
  const marks = useMemo((): EvidenceMark[] => results.flatMap((result) => result.statements.flatMap((statement) => statement.evidence
    .filter((span) => span.segment.split("#")[0] === shown)
    .map((span) => ({ key: `${result.source}#${String(statement.number)}`, start: span.start, end: span.end })))), [results, shown]);

  const linesOf = (keys: readonly string[]) => [...new Set(keys.flatMap((key) => lineKeys.get(key) ?? [key]))];
  const focus = {
    highlighted: new Set(pointed ?? selected ?? []),
    selected: new Set(selected ?? []),
    onPoint: (keys: readonly string[] | null) => { setPointed(keys === null ? null : linesOf(keys)); },
    onSelect: (keys: readonly string[]) => {
      const lines = linesOf(keys);
      const again = selected !== null && lines.every((key) => selected.includes(key));
      setSelected(again ? null : lines);
      if (again) return;
      // The other side comes into view: the line for clicked words, the words for a clicked line.
      requestAnimationFrame(() => {
        container.current?.querySelector(`[data-statements~=${JSON.stringify(lines[0] ?? "")}]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
        container.current?.querySelector(".app-evidence-highlighted")?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      });
    },
  };
  const statementsOf = (key: string, items: typeof unplaced) => <PartStatements key={key} items={items} individuals={individuals} focus={focus} askInProject={askInProject} extra={statementExtra} />;
  return (
    <div ref={container} onKeyDown={(event) => { if (event.key === "Escape") setSelected(null); }}>
      <DocumentParts
        document={document}
        annotations={annotations}
        evidence={marks.length === 0 ? null : { marks, ...focus }}
        asides={new Map([...byPart].map(([segmentPath, items]) => [segmentPath, statementsOf(segmentPath, items)]))}
        askInProject={askInProject}
        askWith={askWith}
      />
      {unplaced.length === 0 ? null : (
        <section className="space-y-1 py-3">
          <h4 className="app-label text-xs">{t("sources.document.regest.unplaced")}</h4>
          {statementsOf("unplaced", unplaced)}
        </section>
      )}
    </div>
  );
}
